// ================================================================
// OrgPanel — overlay «تیم و نقش‌ها» + سطل آشغال (اعضا/نقش‌ها)
// ================================================================
import './org.css';
import './org-dark.css';
import { t } from '../../i18n/index.js';
import { icon, escapeHtml, delegate } from '../../utils/dom.js';
import { getUser, getPermissions } from '../../core/auth.js';
import {
  listMembers, setMemberRole, setMemberStatus, setMemberName,
  listRoles, createRole, updateRole,
  listInvitations, createInvitation, revokeInvitation,
  removeMember, listMembersTrash, restoreMember, purgeMember,
  deleteRole, listRolesTrash, restoreRole, purgeRole,
} from '../../core/org.js';
import { date as fmtDate, dateTime as fmtDateTime } from '../../utils/format.ts';

const PERM_GROUPS = [
  { key: 'scenarios',   perms: ['scenarios:read', 'scenarios:write', 'scenarios:delete', 'scenarios:run'] },
  { key: 'connections', perms: ['connections:read', 'connections:write', 'connections:delete'] },
  { key: 'executions',  perms: ['executions:read'] },
  { key: 'members',     perms: ['members:read', 'members:invite', 'members:write', 'members:remove'] },
  { key: 'roles',       perms: ['roles:read', 'roles:write'] },
  { key: 'documents',   perms: ['documents:create', 'documents:send'] },
  { key: 'customers',   perms: ['customers:read', 'customers:create', 'team:activity:read'] },
  { key: 'email',       perms: ['email:read'] },  
  { key: 'whatsapp',    perms: ['whatsapp:send'] },
];
const PLATFORM_GROUP = { key: 'platform', perms: ['platform:orgs:read', 'platform:orgs:write', 'platform:audit:read'] };

let overlay = null;
let perms = new Set();
let isPlatform = false;
const s = {
  tab: 'members',
  subview: null, // null | 'members-trash' | 'roles-trash'
  members: [], roles: [], invitations: [],
  trashMembers: [], trashRoles: [],
  loading: false, error: '',
  inviting: false, inviteBusy: false, inviteError: '', inviteUrl: '',
  inviteMailSent: false, inviteEmail: '',
  roleForm: null,
  confirm: { type: '', id: '' },
};

function can(p) { return perms.has(p); }
function confirming(type, id) { return s.confirm.type === type && s.confirm.id === id; }
function setConfirm(type, id) { s.confirm = { type, id }; }
function clearConfirm() { s.confirm = { type: '', id: '' }; }

let onCloseCb = null;
export function openOrgPanel(startTab, onClose) {
  if (overlay) return;
  onCloseCb = onClose || null;
  const u = getUser();
  perms = getPermissions();
  isPlatform = u?.organization?.isPlatform === true;
  overlay = document.createElement('div');
  overlay.className = 'og-overlay';
  document.body.appendChild(overlay);
  document.body.style.overflow = 'hidden';
  delegate(overlay, '[data-action]', 'click', onAction);
  overlay.addEventListener('submit', onSubmit);
  overlay.addEventListener('change', onChange);
s.tab = startTab || (can('members:read') ? 'members' : (can('roles:read') ? 'roles' : 'members'));
  s.subview = null; s.inviting = false; s.inviteUrl = ''; s.roleForm = null; s.error = ''; clearConfirm();
  loadAll();
}

function close() {
  if (!overlay) return;
  overlay.remove(); overlay = null;
  document.body.style.overflow = '';
  if (onCloseCb) { const cb = onCloseCb; onCloseCb = null; cb(); }
}

async function loadAll() {
  s.loading = true; s.error = ''; clearConfirm(); draw();
  try {
    const [m, r, inv] = await Promise.all([
      can('members:read') ? listMembers() : Promise.resolve({ members: [] }),
      (can('roles:read') || can('members:write') || can('members:invite')) ? listRoles() : Promise.resolve({ roles: [] }),
      can('members:read') ? listInvitations() : Promise.resolve({ invitations: [] }),
    ]);
    s.members = m.members || [];
    s.roles = r.roles || [];
    s.invitations = inv.invitations || [];
  } catch (e) { s.error = e.message; }
  finally { s.loading = false; draw(); }
}

async function loadMembersTrash() {
  s.loading = true; s.error = ''; clearConfirm(); draw();
  try { s.trashMembers = (await listMembersTrash()).members || []; }
  catch (e) { s.error = e.message; }
  finally { s.loading = false; draw(); }
}
async function loadRolesTrash() {
  s.loading = true; s.error = ''; clearConfirm(); draw();
  try { s.trashRoles = (await listRolesTrash()).roles || []; }
  catch (e) { s.error = e.message; }
  finally { s.loading = false; draw(); }
}

async function run(fn, after) {
  try { await fn(); await after(); }
  catch (err) { s.error = err.message; draw(); }
}

// ── رویدادها ──
async function onAction(e, el) {
  const a = el.dataset.action;
  const id = el.dataset.id;
  switch (a) {
    case 'close': return close();
    case 'tab-members': s.tab = 'members'; s.subview = null; return draw();
    case 'tab-roles': s.tab = 'roles'; s.subview = null; return draw();
    case 'members-trash': s.subview = 'members-trash'; return loadMembersTrash();
    case 'roles-trash': s.subview = 'roles-trash'; return loadRolesTrash();
    case 'back-list': s.subview = null; clearConfirm(); return draw();

    case 'invite': s.inviting = true; s.inviteError = ''; s.inviteUrl = ''; return draw();
    case 'cancel-invite': s.inviting = false; return draw();
    case 'dismiss-invite': s.inviteUrl = ''; return draw();
    case 'copy': {
      try { await navigator.clipboard.writeText(el.dataset.url); } catch { /* blocked */ }
      const old = el.textContent; el.textContent = t('org.copied');
      setTimeout(() => { if (el.isConnected) el.textContent = old; }, 1500);
      return;
    }
    case 'revoke': el.disabled = true; return run(() => revokeInvitation(id), loadAll);
    case 'toggle-member-status': {
      const next = el.dataset.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE';
      el.disabled = true; return run(() => setMemberStatus(id, next), loadAll);
    }
    case 'edit-member-name': {
      const cur = el.dataset.name || '';
      const val = window.prompt(t('org.editNamePrompt'), cur);
      if (val === null) return;
      const trimmed = val.trim();
      if (!trimmed || trimmed === cur) return;
      el.disabled = true;
      return run(() => setMemberName(id, trimmed), loadAll);
    }

    case 'new-role': s.roleForm = { id: null, busy: false, error: '' }; return draw();
    case 'edit-role': s.roleForm = { id, busy: false, error: '' }; return draw();
    case 'cancel-role': s.roleForm = null; return draw();

    // تأییدهای دومرحله‌ای
    case 'ask-remove-member': setConfirm('remove-member', id); return draw();
    case 'ask-delete-role': setConfirm('delete-role', id); return draw();
    case 'ask-purge-member': setConfirm('purge-member', id); return draw();
    case 'ask-purge-role': setConfirm('purge-role', id); return draw();
    case 'cancel-confirm': clearConfirm(); return draw();

    case 'do-remove-member': el.disabled = true; return run(() => removeMember(id), loadAll);
    case 'do-delete-role': el.disabled = true; return run(() => deleteRole(id), loadAll);

    case 'restore-member': el.disabled = true; return run(() => restoreMember(id), loadMembersTrash);
    case 'do-purge-member': el.disabled = true; return run(() => purgeMember(id), loadMembersTrash);
    case 'restore-role': el.disabled = true; return run(() => restoreRole(id), loadRolesTrash);
    case 'do-purge-role': el.disabled = true; return run(() => purgeRole(id), loadRolesTrash);
  }
}

async function onChange(e) {
  const el = e.target;
  if (el.matches('select[data-member-role]')) {
    el.disabled = true;
    return run(() => setMemberRole(el.dataset.memberRole, el.value || null), loadAll);
  }
}

async function onSubmit(e) {
  if (e.target.id === 'og-invite-form') {
    e.preventDefault();
    const f = e.target.elements;
    const email = f.namedItem('email').value.trim();
    const roleId = f.namedItem('roleId').value || null;
    s.inviteBusy = true; s.inviteError = ''; draw();
    try {
      const r = await createInvitation(email, roleId);
      s.inviteUrl = r.inviteUrl; s.inviteMailSent = !!r.mailSent; s.inviteEmail = email;
      s.inviting = false; s.inviteBusy = false;
      await loadAll();
    } catch (err) {
      s.inviteBusy = false;
      s.inviteError = err.status === 409 ? t('org.errors.alreadyMember') : (err.message || t('org.errors.generic'));
      draw();
    }
    return;
  }
  if (e.target.id === 'og-role-form') {
    e.preventDefault();
    const f = e.target;
    const name = f.elements.namedItem('name').value.trim();
    const description = f.elements.namedItem('description').value.trim();
    const checked = [...f.querySelectorAll('input[name="perm"]:checked')].map((c) => c.value);
    s.roleForm.busy = true; s.roleForm.error = ''; draw();
    try {
      const payload = { name, description, permissions: checked };
      if (s.roleForm.id) await updateRole(s.roleForm.id, payload);
      else await createRole(payload);
      s.roleForm = null;
      await loadAll();
    } catch (err) {
      s.roleForm.busy = false;
      s.roleForm.error = err.message || t('org.errors.generic');
      draw();
    }
    return;
  }
}

// ── رندر ──
function draw() {
  if (!overlay) return;
  const showRolesTab = can('roles:read');
  let body;
  if (s.loading) body = muted(t('org.loading'));
  else if (s.subview === 'members-trash') body = membersTrashView();
  else if (s.subview === 'roles-trash') body = rolesTrashView();
  else body = s.tab === 'roles' ? rolesTab() : membersTab();

  overlay.innerHTML = `
    <div class="og-backdrop" data-action="close"></div>
    <div class="og-panel" role="dialog" aria-modal="true">
      <div class="og-head">
        <div class="og-head-title">${icon('groups', 22)}<span>${escapeHtml(t('org.title'))}</span></div>
        <button class="og-icon-btn" data-action="close">${icon('close', 20)}</button>
      </div>
      <div class="og-tabs">
        <button class="og-tab ${s.tab === 'members' && !s.subview ? 'active' : ''}" data-action="tab-members">${escapeHtml(t('org.tabs.members'))}</button>
        ${showRolesTab ? `<button class="og-tab ${s.tab === 'roles' && !s.subview ? 'active' : ''}" data-action="tab-roles">${escapeHtml(t('org.tabs.roles'))}</button>` : ''}
      </div>
      <div class="og-body">${body}</div>
    </div>`;
}

function muted(txt) { return `<p class="og-muted">${escapeHtml(txt)}</p>`; }
function errBox(msg) { return `<div class="og-error">${icon('error', 16)}<span>${escapeHtml(msg)}</span></div>`; }

function roleOptions(selectedId) {
  const none = `<option value="" ${!selectedId ? 'selected' : ''}>${escapeHtml(t('org.noRole'))}</option>`;
  return none + s.roles.map((r) => `<option value="${escapeHtml(r.id)}" ${r.id === selectedId ? 'selected' : ''}>${escapeHtml(r.name)}</option>`).join('');
}

// ── تب اعضا ──
function membersTab() {
  if (!can('members:read')) return muted(t('org.noAccess'));
  let h = '';
  if (s.error) h += errBox(s.error);

  const tools = [];
  if (can('members:invite') && !s.inviting) tools.push(`<button class="og-btn og-btn-primary" data-action="invite">${icon('person_add', 18)}<span>${escapeHtml(t('org.invite'))}</span></button>`);
  if (can('members:remove')) tools.push(`<button class="og-btn og-btn-ghost" data-action="members-trash">${icon('delete', 18)}<span>${escapeHtml(t('org.trash'))}</span></button>`);

  if (can('members:invite') && s.inviteUrl) h += inviteCreatedBox();
  if (s.inviting) h += inviteFormHtml();
  if (tools.length) h += `<div class="og-toolbar og-toolbar-split">${tools.join('')}</div>`;

  h += `<div class="og-list">${s.members.map(memberRow).join('')}</div>`;

  if (s.invitations.length) {
    h += `<h3 class="og-section-title">${escapeHtml(t('org.pending'))}</h3>`;
    h += `<div class="og-list">${s.invitations.map(invRow).join('')}</div>`;
  }
  return h;
}

function memberRow(m) {
  const me = getUser();
  const isSelf = me && m.id === me.id;
  const canWrite = can('members:write');
  const canRemove = can('members:remove');
  const roleCell = canWrite && !isSelf
    ? `<select class="og-select" data-member-role="${escapeHtml(m.id)}">${roleOptions(m.role ? m.role.id : '')}</select>`
    : `<span class="og-tag">${escapeHtml(m.role ? m.role.name : t('org.noRole'))}</span>`;
  const statusBadge = `<span class="og-badge ${m.status === 'ACTIVE' ? 'og-badge-active' : 'og-badge-disabled'}">${escapeHtml(m.status)}</span>`;
  const statusBtn = (canWrite && !isSelf)
    ? `<button class="og-btn og-btn-sm" data-action="toggle-member-status" data-id="${escapeHtml(m.id)}" data-status="${escapeHtml(m.status)}">${escapeHtml(m.status === 'ACTIVE' ? t('org.disable') : t('org.enable'))}</button>`
    : '';
  let removeCtl = '';
  if (canRemove && !isSelf) {
    removeCtl = confirming('remove-member', m.id)
      ? `<button class="og-btn og-btn-sm og-btn-danger" data-action="do-remove-member" data-id="${escapeHtml(m.id)}">${escapeHtml(t('org.confirmRemove'))}</button>
         <button class="og-btn og-btn-sm og-btn-ghost" data-action="cancel-confirm">${escapeHtml(t('org.cancel'))}</button>`
      : `<button class="og-btn og-btn-sm og-btn-danger" data-action="ask-remove-member" data-id="${escapeHtml(m.id)}">${escapeHtml(t('org.remove'))}</button>`;
  }
  return `
    <div class="og-row">
      <div class="og-row-id">
        <span class="og-row-main">${escapeHtml(m.name || m.email)} ${isSelf ? `<span class="og-you">${escapeHtml(t('org.you'))}</span>` : ''} ${canWrite ? `<button class="og-name-edit" data-action="edit-member-name" data-id="${escapeHtml(m.id)}" data-name="${escapeHtml(m.name || '')}" title="${escapeHtml(t('org.editName'))}">${icon('edit', 14)}</button>` : ''}</span>
        <span class="og-row-sub">${escapeHtml(m.email)}</span>
      </div>
      <div class="og-row-role">${roleCell}</div>
      ${statusBadge}
      ${statusBtn}
      ${removeCtl}
    </div>`;
}

function invRow(inv) {
  const when = fmtDate(inv.expiresAt);
  const revoke = can('members:invite')
    ? `<button class="og-btn og-btn-sm og-btn-danger" data-action="revoke" data-id="${escapeHtml(inv.id)}">${escapeHtml(t('org.revoke'))}</button>` : '';
  return `
    <div class="og-row og-row-pending">
      <div class="og-row-id">
        <span class="og-row-main">${escapeHtml(inv.email)}</span>
        <span class="og-row-sub">${escapeHtml(t('org.expires'))} ${escapeHtml(when)}</span>
      </div>
      <span class="og-tag">${escapeHtml(inv.role ? inv.role.name : t('org.noRole'))}</span>
      <span class="og-badge og-badge-pending">${escapeHtml(t('org.pendingBadge'))}</span>
      ${revoke}
    </div>`;
}

function inviteFormHtml() {
  return `
    <form id="og-invite-form" class="og-form">
      ${s.inviteError ? errBox(s.inviteError) : ''}
      <div class="og-form-row">
        <label class="og-field og-grow"><span>${escapeHtml(t('org.inviteEmail'))}</span>
          <input class="og-input" type="email" name="email" required autocomplete="off" /></label>
        <label class="og-field"><span>${escapeHtml(t('org.inviteRole'))}</span>
          <select class="og-select" name="roleId">${roleOptions('')}</select></label>
      </div>
      <div class="og-row-gap">
        <button type="submit" class="og-btn og-btn-primary" ${s.inviteBusy ? 'disabled' : ''}>${escapeHtml(s.inviteBusy ? t('org.send') + '…' : t('org.send'))}</button>
        <button type="button" class="og-btn og-btn-ghost" data-action="cancel-invite">${escapeHtml(t('org.cancel'))}</button>
      </div>
    </form>`;
}

function inviteCreatedBox() {
  return `
    <div class="og-success">
      <div class="og-success-head">${icon('check_circle', 18)}<span>${escapeHtml(t('org.inviteCreated'))}</span></div>
      <p class="og-muted">${escapeHtml(s.inviteMailSent
        ? t('org.inviteMailSent', { email: s.inviteEmail })
        : t('org.inviteMailFailed'))}</p>
      <div class="og-invite">
        <input class="og-input" readonly value="${escapeHtml(s.inviteUrl)}" onclick="this.select()" />
        <button class="og-btn" data-action="copy" data-url="${escapeHtml(s.inviteUrl)}">${escapeHtml(t('org.copy'))}</button>
      </div>
      <button class="og-btn og-btn-ghost" data-action="dismiss-invite">${escapeHtml(t('org.done'))}</button>
    </div>`;
}

// ── تب نقش‌ها ──
function rolesTab() {
  if (!can('roles:read')) return muted(t('org.noAccess'));
  let h = '';
  if (s.error) h += errBox(s.error);
  if (s.roleForm) h += roleFormHtml();
  else {
    const tools = [];
    if (can('roles:write')) tools.push(`<button class="og-btn og-btn-primary" data-action="new-role">${icon('add', 18)}<span>${escapeHtml(t('org.newRole'))}</span></button>`);
    if (can('roles:write')) tools.push(`<button class="og-btn og-btn-ghost" data-action="roles-trash">${icon('delete', 18)}<span>${escapeHtml(t('org.trash'))}</span></button>`);
    if (tools.length) h += `<div class="og-toolbar og-toolbar-split">${tools.join('')}</div>`;
  }
  h += `<div class="og-list">${s.roles.map(roleCard).join('')}</div>`;
  return h;
}

function roleCard(r) {
  const sys = r.isSystem ? `<span class="og-badge og-badge-system">${escapeHtml(t('org.systemBadge'))}</span>` : '';
  const canWrite = can('roles:write') && !r.isSystem;
  const editBtn = canWrite ? `<button class="og-btn og-btn-sm" data-action="edit-role" data-id="${escapeHtml(r.id)}">${escapeHtml(t('org.edit'))}</button>` : '';
  let delCtl = '';
  if (canWrite) {
    delCtl = confirming('delete-role', r.id)
      ? `<button class="og-btn og-btn-sm og-btn-danger" data-action="do-delete-role" data-id="${escapeHtml(r.id)}">${escapeHtml(t('org.confirmDeleteRole'))}</button>
         <button class="og-btn og-btn-sm og-btn-ghost" data-action="cancel-confirm">${escapeHtml(t('org.cancel'))}</button>`
      : `<button class="og-btn og-btn-sm og-btn-danger" data-action="ask-delete-role" data-id="${escapeHtml(r.id)}">${escapeHtml(t('org.delete'))}</button>`;
  }
  return `
    <div class="og-row">
      <div class="og-row-id">
        <span class="og-row-main">${escapeHtml(r.name)} ${sys}</span>
        <span class="og-row-sub">${escapeHtml(r.description || '')}</span>
      </div>
      <span class="og-tag">${(r.permissions ? r.permissions.length : 0)} ${escapeHtml(t('org.permsCount'))}</span>
      <span class="og-tag">${(r._count ? r._count.users : 0)} ${escapeHtml(t('org.usersCount'))}</span>
      ${editBtn}
      ${delCtl}
    </div>`;
}

function roleFormHtml() {
  const editing = s.roleForm.id ? s.roles.find((r) => r.id === s.roleForm.id) : null;
  const current = new Set(editing ? editing.permissions : []);
  const groups = isPlatform ? [...PERM_GROUPS, PLATFORM_GROUP] : PERM_GROUPS;
  const groupsHtml = groups.map((g) => `
    <div class="og-perm-group">
      <div class="og-perm-group-title">${escapeHtml(t('org.permGroups.' + g.key))}</div>
      <div class="og-perm-list">
        ${g.perms.map((p) => `
          <label class="og-check">
            <input type="checkbox" name="perm" value="${escapeHtml(p)}" ${current.has(p) ? 'checked' : ''} />
            <span>${escapeHtml(t('org.perms.' + p))}</span>
          </label>`).join('')}
      </div>
    </div>`).join('');

  return `
    <form id="og-role-form" class="og-form og-role-form">
      ${s.roleForm.error ? errBox(s.roleForm.error) : ''}
      <div class="og-form-row">
        <label class="og-field og-grow"><span>${escapeHtml(t('org.roleName'))}</span>
          <input class="og-input" name="name" required autocomplete="off" value="${escapeHtml(editing ? editing.name : '')}" /></label>
      </div>
      <label class="og-field"><span>${escapeHtml(t('org.roleDescription'))}</span>
        <input class="og-input" name="description" autocomplete="off" value="${escapeHtml(editing ? (editing.description || '') : '')}" /></label>
      <div class="og-perm-groups">${groupsHtml}</div>
      <div class="og-row-gap">
        <button type="submit" class="og-btn og-btn-primary" ${s.roleForm.busy ? 'disabled' : ''}>${escapeHtml(s.roleForm.busy ? t('org.save') + '…' : t('org.save'))}</button>
        <button type="button" class="og-btn og-btn-ghost" data-action="cancel-role">${escapeHtml(t('org.cancel'))}</button>
      </div>
    </form>`;
}

// ── سطل آشغالِ اعضا ──
function membersTrashView() {
  let h = trashHeader(t('org.trashMembersTitle'));
  if (s.error) h += errBox(s.error);
  if (!s.trashMembers.length) return h + muted(t('org.trashEmpty'));
  h += `<div class="og-list">${s.trashMembers.map((m) => {
    const when = fmtDateTime(m.deletedAt);
    const purge = confirming('purge-member', m.id)
      ? `<button class="og-btn og-btn-sm og-btn-danger" data-action="do-purge-member" data-id="${escapeHtml(m.id)}">${escapeHtml(t('org.confirmForever'))}</button>
         <button class="og-btn og-btn-sm og-btn-ghost" data-action="cancel-confirm">${escapeHtml(t('org.cancel'))}</button>`
      : `<button class="og-btn og-btn-sm og-btn-danger" data-action="ask-purge-member" data-id="${escapeHtml(m.id)}">${escapeHtml(t('org.deleteForever'))}</button>`;
    return `<div class="og-row">
      <div class="og-row-id">
        <span class="og-row-main">${escapeHtml(m.name || m.email)}</span>
        <span class="og-row-sub">${escapeHtml(m.email)} · ${escapeHtml(t('org.deletedAt'))} ${escapeHtml(when)}</span>
      </div>
      <button class="og-btn og-btn-sm" data-action="restore-member" data-id="${escapeHtml(m.id)}">${icon('restore_from_trash', 16)}<span>${escapeHtml(t('org.restore'))}</span></button>
      ${purge}
    </div>`;
  }).join('')}</div>`;
  return h;
}

// ── سطل آشغالِ نقش‌ها ──
function rolesTrashView() {
  let h = trashHeader(t('org.trashRolesTitle'));
  if (s.error) h += errBox(s.error);
  if (!s.trashRoles.length) return h + muted(t('org.trashEmpty'));
  h += `<div class="og-list">${s.trashRoles.map((r) => {
    const when = fmtDateTime(r.deletedAt);
    const purge = confirming('purge-role', r.id)
      ? `<button class="og-btn og-btn-sm og-btn-danger" data-action="do-purge-role" data-id="${escapeHtml(r.id)}">${escapeHtml(t('org.confirmForever'))}</button>
         <button class="og-btn og-btn-sm og-btn-ghost" data-action="cancel-confirm">${escapeHtml(t('org.cancel'))}</button>`
      : `<button class="og-btn og-btn-sm og-btn-danger" data-action="ask-purge-role" data-id="${escapeHtml(r.id)}">${escapeHtml(t('org.deleteForever'))}</button>`;
    return `<div class="og-row">
      <div class="og-row-id">
        <span class="og-row-main">${escapeHtml(r.name)}</span>
        <span class="og-row-sub">${escapeHtml(r.description || '')} · ${escapeHtml(t('org.deletedAt'))} ${escapeHtml(when)}</span>
      </div>
      <button class="og-btn og-btn-sm" data-action="restore-role" data-id="${escapeHtml(r.id)}">${icon('restore_from_trash', 16)}<span>${escapeHtml(t('org.restore'))}</span></button>
      ${purge}
    </div>`;
  }).join('')}</div>`;
  return h;
}

function trashHeader(title) {
  return `<div class="og-trash-head">
    <button class="og-icon-btn og-icon-btn-dark" data-action="back-list">${icon('arrow_back', 20)}</button>
    <h3 class="og-trash-title">${icon('delete', 18)} ${escapeHtml(title)}</h3>
  </div>`;
}
